/**
 * Environment Configuration Utility
 * Provides secure access to environment variables with fallbacks
 */

// Firebase Configuration with fallbacks for smooth transition
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyA5nRSOMzVYmq_XpgdGlYMHXQCLvPbjggs",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "tioassist.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "tioassist",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "tioassist.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "601162797903",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:601162797903:web:0032e77a5ee8ff3cf0ab3a",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-WX9BMGF2SC",
};

// VAPID Key for FCM with fallback
export const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || 'BGEMYjsxouyuO5i0hHqGlibE5MCSpjYvbis5FJhnuA-g01zk4Vy9IcoTNFvz53JQjDvmLhQYrvkNK1agYiynxtE';

// Gemini AI Configuration
export const geminiConfig = {
  // Model configuration
  model: 'gemini-2.5-flash-lite',
  
  // Generation parameters optimized for chat
  generationConfig: {
    temperature: import.meta.env.VITE_GEMINI_TEMPERATURE ? 
      parseFloat(import.meta.env.VITE_GEMINI_TEMPERATURE) : 0.7,
    topK: import.meta.env.VITE_GEMINI_TOP_K ? 
      parseInt(import.meta.env.VITE_GEMINI_TOP_K) : 40,
    topP: import.meta.env.VITE_GEMINI_TOP_P ? 
      parseFloat(import.meta.env.VITE_GEMINI_TOP_P) : 0.95,
    maxOutputTokens: import.meta.env.VITE_GEMINI_MAX_OUTPUT_TOKENS ? 
      parseInt(import.meta.env.VITE_GEMINI_MAX_OUTPUT_TOKENS) : 1024,
    candidateCount: 1 // Always 1 for efficiency
  },
  
  // Rate limiting configuration
  rateLimits: {
    anonymous: import.meta.env.VITE_GEMINI_RATE_LIMIT_ANONYMOUS ? 
      parseInt(import.meta.env.VITE_GEMINI_RATE_LIMIT_ANONYMOUS) : 10,
    authenticated: import.meta.env.VITE_GEMINI_RATE_LIMIT_AUTH ? 
      parseInt(import.meta.env.VITE_GEMINI_RATE_LIMIT_AUTH) : 20,
    windowMs: 60000 // 1 minute window
  },
  
  // Token limits for cost control
  tokenLimits: {
    maxInputTokens: import.meta.env.VITE_GEMINI_MAX_INPUT_TOKENS ? 
      parseInt(import.meta.env.VITE_GEMINI_MAX_INPUT_TOKENS) : 800,
    maxMessageLength: import.meta.env.VITE_GEMINI_MAX_MESSAGE_LENGTH ? 
      parseInt(import.meta.env.VITE_GEMINI_MAX_MESSAGE_LENGTH) : 5000
  },
  
  // Cache configuration
  cache: {
    enabled: import.meta.env.VITE_GEMINI_CACHE_ENABLED !== 'false',
    maxSize: import.meta.env.VITE_GEMINI_CACHE_MAX_SIZE ? 
      parseInt(import.meta.env.VITE_GEMINI_CACHE_MAX_SIZE) : 100,
    ttl: import.meta.env.VITE_GEMINI_CACHE_TTL ? 
      parseInt(import.meta.env.VITE_GEMINI_CACHE_TTL) : 3600000 // 1 hour
  }
};

// App Check Configuration for production security
export const appCheckConfig = {
  enabled: import.meta.env.VITE_APP_CHECK_ENABLED === 'true' || import.meta.env.NODE_ENV === 'production',
  siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // Test key fallback
  isTokenAutoRefreshEnabled: true
};

// WebRTC Configuration
export const getWebRTCConfig = () => {
  const turnUsername = import.meta.env.VITE_TURN_USERNAME || '9f24237eef08d63be9f50c85';
  const turnCredential = import.meta.env.VITE_TURN_CREDENTIAL || 'bcSTypx8qHWJ7hH9';
  const turnUrl = import.meta.env.VITE_TURN_URL || 'turn:standard.relay.metered.ca:80';

  const config = {
    iceServers: [
      {
        urls: [
          'stun:stun.l.google.com:19302',
          'stun:stun1.l.google.com:19302',
          'stun:stun2.l.google.com:19302',
          'stun:stun3.l.google.com:19302',
          'stun:stun4.l.google.com:19302',
        ]
      }
    ],
    iceCandidatePoolSize: 10,
  };

  // Only add TURN servers if credentials are available
  if (turnUsername && turnCredential) {
    config.iceServers.push(
      {
        urls: turnUrl,
        username: turnUsername,
        credential: turnCredential,
      },
      {
        urls: turnUrl + '?transport=tcp',
        username: turnUsername,
        credential: turnCredential,
      },
      {
        urls: turnUrl.replace('turn:', 'turns:').replace(':80', ':443'),
        username: turnUsername,
        credential: turnCredential,
      },
      {
        urls: turnUrl.replace('turn:', 'turns:').replace(':80', ':443') + '?transport=tcp',
        username: turnUsername,
        credential: turnCredential,
      }
    );
  }

  return config;
};

// Development/Debug Configuration
export const isDevelopment = import.meta.env.VITE_ENVIRONMENT !== 'production';
export const isDebugMode = import.meta.env.VITE_DEBUG_MODE === 'true';

// Helper function to safely log in development only
export const devLog = (...args) => {
  if (isDevelopment && isDebugMode) {
    console.log('[DEV]', ...args);
  }
};

// Helper function to validate required environment variables
export const validateEnvVars = () => {
  // Since we now have fallbacks, this function mainly checks if env vars are preferred over fallbacks
  const envVarsPresent = import.meta.env.VITE_FIREBASE_API_KEY &&
                         import.meta.env.VITE_FIREBASE_AUTH_DOMAIN &&
                         import.meta.env.VITE_FIREBASE_PROJECT_ID;

  if (!envVarsPresent) {
    console.warn('Using fallback Firebase configuration. For production, set environment variables.');
    console.warn('Copy .env.local.example to .env.local and customize for your environment.');
  }

  return true; // Always return true since we have fallbacks
};

// Export all env vars for debugging (development only)
export const getAllEnvVars = () => {
  if (!isDevelopment) {
    return 'Environment variables only available in development mode';
  }

  return Object.keys(import.meta.env)
    .filter(key => key.startsWith('VITE_'))
    .reduce((acc, key) => {
      // Mask sensitive values
      if (key.includes('CREDENTIAL') || key.includes('KEY')) {
        acc[key] = '***MASKED***';
      } else {
        acc[key] = import.meta.env[key];
      }
      return acc;
    }, {});
};
